/**
 * One client's ledger on the Rozliczenia tab: the history page by page, the client's schedule that
 * names its sessions, and what the four acts pick from. The acts themselves are
 * `features/credit-ledger`'s `useLedgerAct`, which the client card opens too.
 *
 * ── Two reads, one gate ────────────────────────────────────────────────────────────────────────
 * The history (`customerLedgerPagesQueryOptions`) and the client's schedule over the forgiveness
 * window, widened back to the oldest movement in hand (`ledgerSessionDays`). The schedule is what
 * names a deduction („Trening · pt 18.09, 10:00") and what the forgiveness dialog picks from, so
 * the card waits for both; a later page that widens the window keeps the last answer on screen
 * while it is read.
 *
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { customerScheduleQueryOptions, scheduleWindow } from '~/entities/calendar-event';
import {
  customerLedgerPagesQueryOptions,
  useLedgerAct,
  type LedgerActState,
  type LedgerStanding,
} from '~/features/credit-ledger';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { readBookingSession, type BookingSession } from '../model/bookingSession';
import {
  forgivableSessions,
  ledgerRows,
  ledgerSessionDays,
  type LedgerRow,
} from '../model/creditLedger';
import { FORGIVENESS_WINDOW_DAYS } from '../model/sessionActions';
import { wallReading, type CivilDay } from '../model/trainerClock';

export type ClientLedgerContent =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'failed' }
  | {
      readonly kind: 'ready';
      readonly standing: LedgerStanding;
      readonly since: string | null;
      readonly rows: readonly LedgerRow[];
    };

export interface ClientLedgerState {
  readonly gate: SkeletonGate;
  readonly content: ClientLedgerContent;
  readonly retry: () => void;
  readonly hasOlder: boolean;
  readonly loadingOlder: boolean;
  readonly olderFailed: boolean;
  readonly loadOlder: () => void;
  /** The act dialog's state — `features/credit-ledger`'s, over this ledger's own standing. */
  readonly act: LedgerActState;
  /** The charges that may still be taken back — `null` while the schedule is read. */
  readonly forgivable: readonly BookingSession[] | null;
  readonly forgivableFailed: boolean;
  /** The client's schedule did not answer: the history's sessions go unnamed until it does. */
  readonly sessionsFailed: boolean;
  readonly retrySessions: () => void;
}

/**
 * The ledger's two reads for one client, and whether they are still out. The card draws from them;
 * the Rozliczenia tab reads the same two for the client it opens on, so the tab arrives with that
 * client's history already in hand instead of drawing the card a second time when it lands.
 * `customerId` 0 reads nothing.
 */
export function useClientLedgerReads(
  customerId: number,
  context: { readonly zone: string; readonly today: CivilDay },
) {
  const ledger = useInfiniteQuery(customerLedgerPagesQueryOptions(customerId));
  const pages = ledger.data?.pages ?? [];
  const movements = pages.flatMap((page) => page.movements ?? []);
  const oldestAt = movements.at(-1)?.at;
  const oldest =
    oldestAt === undefined || Number.isNaN(Date.parse(oldestAt))
      ? null
      : wallReading(Date.parse(oldestAt), context.zone).day;
  const days = ledgerSessionDays(context.today, oldest, FORGIVENESS_WINDOW_DAYS);
  const schedule = useQuery({
    ...customerScheduleQueryOptions(customerId, scheduleWindow(days.first, days.last)),
    placeholderData: keepPreviousData,
  });
  return {
    ledger,
    schedule,
    first: pages[0],
    movements,
    pending:
      (ledger.isPending && ledger.errorUpdateCount === 0) ||
      (schedule.isPending && schedule.errorUpdateCount === 0),
  };
}

export function useClientLedger(
  customerId: number,
  context: { readonly zone: string; readonly today: CivilDay; readonly nowMs: number },
  announce: (message: string) => void,
): ClientLedgerState {
  const { t } = useTranslation();

  const { ledger, schedule, first, movements, pending } = useClientLedgerReads(customerId, context);
  const sessions = (schedule.data ?? []).map((event) => readBookingSession(event, context.zone));

  const gate = useSkeletonGate(pending);

  const standing: LedgerStanding = {
    balance: first?.balance ?? 0,
    sessionsOwed: first?.sessionsOwed ?? 0,
  };
  const act = useLedgerAct(customerId, standing, announce);
  const content: ClientLedgerContent = gate.waiting
    ? { kind: 'waiting' }
    : first === undefined
      ? { kind: 'failed' }
      : {
          kind: 'ready',
          standing,
          since: first.ledgerSince ?? null,
          rows: ledgerRows(
            movements,
            {
              zone: context.zone,
              sessions: new Map(sessions.map((session) => [session.id, session])),
            },
            t,
          ),
        };

  return {
    gate,
    content,
    retry: () => {
      if (ledger.isError) void ledger.refetch();
      if (schedule.isError) void schedule.refetch();
    },
    hasOlder: ledger.hasNextPage,
    loadingOlder: ledger.isFetchingNextPage,
    olderFailed: ledger.isFetchNextPageError,
    loadOlder: () => {
      if (!ledger.isFetchingNextPage) void ledger.fetchNextPage();
    },
    act,
    forgivable:
      schedule.data === undefined
        ? null
        : forgivableSessions(sessions, movements, context.nowMs, context.zone),
    forgivableFailed: schedule.data === undefined && schedule.isError,
    sessionsFailed: schedule.isError,
    retrySessions: () => {
      void schedule.refetch();
    },
  };
}
